import type {
  CheckoutSplitPlanReadModel,
  CombinedCheckoutManualPaymentMethod,
  CreateCombinedCheckoutRequest,
  OperatorSessionReadModel,
  OrderDetail,
  TableSnapshot,
} from "@billiard/contracts";
import { Banknote, Check, LockKeyhole, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type CombinedCheckoutBaselineItem,
  combinedCheckoutOutcomeIsUnknown,
  createCombinedCheckout,
  getCheckoutSplitPlan,
  getCombinedCheckoutByRequest,
  getOrder,
} from "./api";
import { businessErrorMessage } from "./businessErrorMessage";
import {
  beginCombinedCheckoutDispatch,
  type CombinedCheckoutCommand,
  type CombinedCheckoutRecovery,
  type CombinedCheckoutRecoveryView,
  claimCombinedCheckoutRecovery,
  clearCombinedCheckoutRecovery,
  combinedCheckoutRecoveryContext,
  finishCombinedCheckoutDispatch,
  markCombinedCheckoutOutcomeUnknown,
  readCombinedCheckoutRecovery,
  reconcileCombinedCheckoutAuthority,
} from "./combinedCheckoutRecovery";

type OperatorRole = OperatorSessionReadModel["operator"]["role"];

interface CandidateFact {
  order: OrderDetail;
  table: TableSnapshot;
  splitPlan: CheckoutSplitPlanReadModel | null;
  reason: string | null;
}

interface CandidateReadFailure {
  table: TableSnapshot;
  reason: string;
}

type CandidateRow = CandidateFact | CandidateReadFailure;

const methodLabels: Record<CombinedCheckoutManualPaymentMethod, string> = {
  CASH: "现金",
  STATIC_WECHAT: "微信收款码",
  STATIC_ALIPAY: "支付宝收款码",
};

function formatFen(value: number): string {
  return `¥${(value / 100).toFixed(2)}`;
}

function orderNumber(order: OrderDetail): string {
  return order.orderNumber ?? order.id;
}

function nextIdempotencyKey(): string {
  const random = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  return `combined-checkout:${random}`;
}

export function combinedCheckoutCandidateReason(
  order: OrderDetail,
  table: TableSnapshot,
  splitPlan: CheckoutSplitPlanReadModel | null,
  expectedScope: { tenantId: string; storeId: string },
): string | null {
  if (table.tenantId !== expectedScope.tenantId || table.storeId !== expectedScope.storeId) {
    return "这张台单不属于当前门店，不能合并。";
  }
  if (
    table.tableId !== order.tableId ||
    table.tableNumber !== order.tableNumber ||
    table.sessionId !== order.sessionId
  ) {
    return "球台与台单身份无法对应，请刷新后再选。";
  }
  if (!order.orderNumber) return "这张旧台单没有可核对的业务单号，不能合并。";
  if (order.status === "CONFLICT" || order.syncState === "CONFLICT") {
    return "这张台单存在冲突，必须先处理冲突。";
  }
  if (order.syncState === "FAILED" || order.syncState === "ISOLATED") {
    return "这张台单状态未安全同步，不能合并收款。";
  }
  if (
    order.billingState !== "ENDED" ||
    order.billingPausedAt !== null ||
    order.endedAt === null ||
    table.billingState !== "ENDED" ||
    table.billingPausedAt !== null
  ) {
    return "这张台单还没有停止计费，请先停表。";
  }
  if (order.status !== "OPEN" || table.state !== "IN_USE") {
    return "这张台单已经结账或球台不在营业中，不能重复收款。";
  }
  if (order.totalFen <= 0) return "这张台单没有待收金额。";
  if (order.paidFen !== 0 || order.payments.length > 0) {
    return "这张台单已经有付款记录，不能跨桌合并。";
  }
  if (order.refundedFen !== 0 || order.refunds.length > 0) {
    return "这张台单已有退款记录，不能跨桌合并。";
  }
  if (splitPlan !== null) return "这张台单已有多人分账计划，不能跨桌合并。";
  if (
    table.sessionStartedAt !== order.startedAt ||
    table.pricingPlanName !== order.priceSnapshot.name ||
    table.estimatedFeeFen !== order.tableFeeFen ||
    table.currentTotalFen !== order.totalFen
  ) {
    return "球台金额与台单金额不一致，请刷新后再选。";
  }
  return null;
}

function methodConsequence(method: CombinedCheckoutManualPaymentMethod): string {
  return method === "CASH"
    ? "现金当面确认：收银员点确认后，所选台单同时结账并进入待清台。"
    : `${methodLabels[method]}待渠道核对：确认顾客已扫码后，所选台单同时结账并进入待清台，系统会生成一条待渠道核对事项。`;
}

export function CombinedCheckoutPanel({
  order,
  table,
  tables,
  operator,
  effectiveRoles,
  disabled,
  active,
  onCompleted,
  onRecoveryLockedChange,
  managedRecoveryView,
  onRecoveryViewChange,
}: {
  order: OrderDetail;
  table: TableSnapshot;
  tables: readonly TableSnapshot[];
  operator: { tenantId: string; storeId: string; id: string };
  effectiveRoles: readonly OperatorRole[];
  disabled: boolean;
  active: boolean;
  onCompleted?: (sessionIds: readonly string[]) => void;
  onRecoveryLockedChange?: (locked: boolean, focusCombined: boolean) => void;
  managedRecoveryView?: CombinedCheckoutRecoveryView;
  onRecoveryViewChange?: (view: CombinedCheckoutRecoveryView) => void;
}): React.JSX.Element {
  const canCollect = effectiveRoles.includes("MANAGER") || effectiveRoles.includes("CASHIER");
  const context = useMemo(() => combinedCheckoutRecoveryContext(operator), [operator]);
  const [localRecoveryView, setLocalRecoveryView] = useState<CombinedCheckoutRecoveryView>(() =>
    readCombinedCheckoutRecovery(context),
  );
  const recoveryView = managedRecoveryView ?? localRecoveryView;
  const setRecoveryView = useCallback(
    (view: CombinedCheckoutRecoveryView): void => {
      setLocalRecoveryView(view);
      onRecoveryViewChange?.(view);
    },
    [onRecoveryViewChange],
  );
  const [rows, setRows] = useState<CandidateRow[]>([]);
  const [selected, setSelected] = useState<Set<string>>(() => new Set([order.sessionId]));
  const [method, setMethod] = useState<CombinedCheckoutManualPaymentMethod>("CASH");
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{
    kind: "SUCCESS" | "ERROR" | "UNKNOWN";
    message: string;
  } | null>(null);
  const completedRef = useRef(onCompleted);
  completedRef.current = onCompleted;
  const candidateSourceRef = useRef({ order, table, tables });
  candidateSourceRef.current = { order, table, tables };

  const loadCandidates = useCallback(async (): Promise<void> => {
    setLoading(true);
    const source = candidateSourceRef.current;
    const tableById = new Map(source.tables.map((candidate) => [candidate.tableId, candidate]));
    tableById.set(source.table.tableId, source.table);
    const currentTables = [...tableById.values()].filter(
      (candidate) => candidate.state === "IN_USE" && candidate.sessionId !== null,
    );
    const loaded = await Promise.all(
      currentTables.map(async (candidateTable): Promise<CandidateRow> => {
        try {
          const candidateOrder =
            candidateTable.sessionId === source.order.sessionId
              ? source.order
              : await getOrder(candidateTable.sessionId ?? "");
          const splitPlan = await getCheckoutSplitPlan(candidateOrder.sessionId);
          return {
            order: candidateOrder,
            table: candidateTable,
            splitPlan,
            reason: combinedCheckoutCandidateReason(candidateOrder, candidateTable, splitPlan, {
              tenantId: operator.tenantId,
              storeId: operator.storeId,
            }),
          };
        } catch (error) {
          return {
            table: candidateTable,
            reason: `台单读取失败：${businessErrorMessage(error, "系统没有返回明确原因")}。`,
          };
        }
      }),
    );
    setRows(loaded);
    setSelected((current) => {
      const available = new Set(
        loaded
          .filter((candidate): candidate is CandidateFact => "order" in candidate)
          .map((candidate) => candidate.order.sessionId),
      );
      const next = new Set([...current].filter((sessionId) => available.has(sessionId)));
      if (available.has(source.order.sessionId)) next.add(source.order.sessionId);
      return next;
    });
    setConfirmed(false);
    setLoading(false);
  }, [operator.storeId, operator.tenantId]);

  const notifyRecoveryLock = useCallback(
    (view: CombinedCheckoutRecoveryView): void => {
      onRecoveryLockedChange?.(view.status !== "NONE", view.status !== "NONE");
    },
    [onRecoveryLockedChange],
  );

  const readAuthority = useCallback(async (): Promise<void> => {
    const current = readCombinedCheckoutRecovery(context);
    setRecoveryView(current);
    notifyRecoveryLock(current);
    if (current.status !== "OWNED" || current.recovery.state !== "OUTCOME_UNKNOWN") return;
    setLoading(true);
    try {
      const result = await getCombinedCheckoutByRequest(
        current.recovery.command.request.idempotencyKey,
        current.recovery.command.request,
        current.recovery.command.expected,
      );
      const resolution = reconcileCombinedCheckoutAuthority(current.recovery, context, result);
      const next = readCombinedCheckoutRecovery(context);
      setRecoveryView(next);
      notifyRecoveryLock(next);
      if (resolution.status === "EXECUTED") {
        setFeedback({
          kind: "SUCCESS",
          message: `已从门店权威记录确认原合并收款完成，共 ${resolution.result.orders.length} 张台单，正在同步；不会再次收款。`,
        });
        completedRef.current?.(resolution.result.orders.map((item) => item.sessionId));
      } else if (resolution.status === "RETRY_ALLOWED") {
        setFeedback({
          kind: "UNKNOWN",
          message:
            "门店权威记录已明确返回没有这笔结果。只能使用下方保存的原请求号重试，不能更换台单、金额或收款方式。",
        });
      } else {
        setFeedback({
          kind: "ERROR",
          message: "权威结果与本机原命令无法安全对应，整批台单继续锁定，请由店长核对。",
        });
      }
    } catch (error) {
      setFeedback({
        kind: "ERROR",
        message: `原合并收款结果读取失败：${businessErrorMessage(error, "系统没有返回明确原因")}。整批台单继续锁定，不能重试或改选。`,
      });
    } finally {
      setLoading(false);
    }
  }, [context, notifyRecoveryLock, setRecoveryView]);

  useEffect(() => {
    const current = readCombinedCheckoutRecovery(context);
    if (
      managedRecoveryView === undefined ||
      JSON.stringify(current) !== JSON.stringify(managedRecoveryView)
    ) {
      setRecoveryView(current);
    }
    notifyRecoveryLock(current);
    if (
      managedRecoveryView === undefined &&
      current.status === "OWNED" &&
      current.recovery.state === "OUTCOME_UNKNOWN"
    ) {
      void readAuthority();
    } else if (current.status !== "NONE" && current.status !== "OWNED") {
      setFeedback({
        kind: "ERROR",
        message:
          current.status === "CONTEXT_MISMATCH"
            ? "本机保存着其他员工或其他门店的合并收款原命令。当前账号不能查看、替换或重试，整批台单已锁定。"
            : "浏览器无法安全读取合并收款保护记录。为防止重复收款，整批台单已锁定。",
      });
    }
  }, [context, managedRecoveryView, notifyRecoveryLock, readAuthority, setRecoveryView]);

  useEffect(() => {
    setRows([]);
    setSelected(new Set([order.sessionId]));
    setConfirmed(false);
  }, [order.sessionId]);

  useEffect(() => {
    if (active && recoveryView.status === "NONE" && rows.length === 0) void loadCandidates();
  }, [active, loadCandidates, recoveryView.status, rows.length]);

  const facts = rows.filter((candidate): candidate is CandidateFact => "order" in candidate);
  const selectedFacts = facts
    .filter((candidate) => selected.has(candidate.order.sessionId))
    .sort((left, right) => left.order.sessionId.localeCompare(right.order.sessionId));
  const totalFen = selectedFacts.reduce((sum, candidate) => sum + candidate.order.totalFen, 0);
  const selectedProblem =
    selectedFacts.find((candidate) => candidate.reason !== null)?.reason ?? null;
  const selectionMessage =
    selectedFacts.length < 2
      ? "请至少选择 2 张台单。"
      : selectedFacts.length > 4
        ? "一次最多选择 4 张台单。"
        : selectedProblem
          ? selectedProblem
          : !Number.isSafeInteger(totalFen) || totalFen <= 0
            ? "所选台单总额无法安全计算。"
            : null;
  const recoveryLocked = recoveryView.status !== "NONE";
  const operationDisabled =
    disabled || !canCollect || loading || submitting || recoveryLocked || selectionMessage !== null;

  const buildCommand = (): CombinedCheckoutCommand | null => {
    if (selectionMessage !== null) return null;
    const request: CreateCombinedCheckoutRequest = {
      contractVersion: "1.0.0",
      orders: selectedFacts.map(({ order: selectedOrder, table: selectedTable }) => ({
        sessionId: selectedOrder.sessionId,
        orderId: selectedOrder.id,
        orderNumber: orderNumber(selectedOrder),
        tableId: selectedOrder.tableId,
        tableNumber: selectedOrder.tableNumber,
        expectedTableRevision: selectedTable.revision,
        expectedAmountFen: selectedOrder.totalFen,
      })),
      expectedTotalFen: totalFen,
      method,
      idempotencyKey: nextIdempotencyKey(),
    };
    const baselines: CombinedCheckoutBaselineItem[] = selectedFacts.map((candidate) => ({
      order: candidate.order,
      table: candidate.table,
      splitPlan: candidate.splitPlan,
    }));
    return {
      request,
      expected: {
        tenantId: operator.tenantId,
        storeId: operator.storeId,
        actorId: operator.id,
        baselines,
      },
    };
  };

  const dispatch = async (recovery: CombinedCheckoutRecovery): Promise<void> => {
    if (!beginCombinedCheckoutDispatch(recovery, context)) {
      setFeedback({ kind: "ERROR", message: "原合并收款请求当前不能安全提交，整批台单保持锁定。" });
      return;
    }
    setSubmitting(true);
    setFeedback(null);
    try {
      const result = await createCombinedCheckout(
        recovery.command.request,
        recovery.command.expected,
      );
      if (!clearCombinedCheckoutRecovery(recovery, context)) {
        setFeedback({
          kind: "ERROR",
          message: "门店已返回成功，但本机无法清除保护记录。整批台单继续锁定，请勿再次收款。",
        });
        return;
      }
      const next = readCombinedCheckoutRecovery(context);
      setRecoveryView(next);
      notifyRecoveryLock(next);
      setFeedback({
        kind: "SUCCESS",
        message:
          recovery.command.request.method === "CASH"
            ? `现金当面确认完成：${result.orders.length} 张台单已结账，球台均进入待清台。`
            : `${methodLabels[recovery.command.request.method]}收款已登记：${result.orders.length} 张台单已结账，球台均进入待清台；请在待核对事项中继续核对渠道到账。`,
      });
      completedRef.current?.(result.orders.map((item) => item.sessionId));
    } catch (error) {
      if (combinedCheckoutOutcomeIsUnknown(error)) {
        markCombinedCheckoutOutcomeUnknown(recovery, context);
        const next = readCombinedCheckoutRecovery(context);
        setRecoveryView(next);
        notifyRecoveryLock(next);
        setFeedback({
          kind: "UNKNOWN",
          message:
            "这批合并收款结果未知。不要再次收款、换账号或改选台单；正在用原请求号读取门店权威结果。",
        });
        if (managedRecoveryView === undefined) await readAuthority();
      } else {
        clearCombinedCheckoutRecovery(recovery, context);
        const next = readCombinedCheckoutRecovery(context);
        setRecoveryView(next);
        notifyRecoveryLock(next);
        setFeedback({
          kind: "ERROR",
          message: `合并收款失败：${businessErrorMessage(error, "系统没有返回明确原因")}。系统没有把这些台单标为已付款，可以核对后重试。`,
        });
      }
    } finally {
      finishCombinedCheckoutDispatch(recovery, context);
      setSubmitting(false);
      setConfirmed(false);
    }
  };

  const submit = (): void => {
    const command = buildCommand();
    if (!command || !confirmed || operationDisabled) return;
    const claimed = claimCombinedCheckoutRecovery(context, command);
    setRecoveryView(claimed);
    notifyRecoveryLock(claimed);
    if (claimed.status !== "OWNED") {
      setFeedback({
        kind: "ERROR",
        message: "无法安全保存这批台单的原命令，系统没有发出收款请求，整批台单已锁定。",
      });
      return;
    }
    void dispatch(claimed.recovery);
  };

  const retryOriginal = (): void => {
    if (recoveryView.status !== "OWNED" || !recoveryView.canRetry) return;
    void dispatch(recoveryView.recovery);
  };

  return (
    <section className="combined-checkout-panel" aria-label="跨桌合并收款">
      <header>
        <div>
          <span className="eyebrow">跨桌合并收款</span>
          <h3>一次收取 2 至 4 张台单</h3>
          <p>仅限已停表、未付款且没有分账或退款的营业台单。确认后逐单结账并进入待清台。</p>
        </div>
        <button
          className="icon-button"
          type="button"
          title="重新读取可合并台单"
          aria-label="重新读取可合并台单"
          disabled={loading || submitting || recoveryLocked}
          onClick={() => {
            setFeedback(null);
            void loadCandidates();
          }}
        >
          <RefreshCw size={17} />
        </button>
      </header>

      {loading && rows.length === 0 ? (
        <div className="loading-state">正在读取可合并台单...</div>
      ) : null}
      <div className="combined-checkout-candidates">
        {rows.map((candidate) => {
          const candidateOrder = "order" in candidate ? candidate.order : null;
          const sessionId =
            candidateOrder?.sessionId ?? candidate.table.sessionId ?? candidate.table.tableId;
          const checked = selected.has(sessionId);
          const rowDisabled =
            !canCollect ||
            recoveryLocked ||
            submitting ||
            (candidate.reason !== null && !checked) ||
            (!checked && selected.size >= 4);
          return (
            <label key={sessionId} className={rowDisabled ? "is-disabled" : ""}>
              <input
                type="checkbox"
                checked={checked}
                disabled={rowDisabled}
                onChange={(event) => {
                  setSelected((current) => {
                    const next = new Set(current);
                    if (event.target.checked) next.add(sessionId);
                    else next.delete(sessionId);
                    return next;
                  });
                  setConfirmed(false);
                  setFeedback(null);
                }}
              />
              <span>
                <strong>{candidate.table.tableNumber} 号台</strong>
                <small>
                  {candidateOrder ? `订单号 ${orderNumber(candidateOrder)}` : "台单未读取"}
                </small>
              </span>
              <b>{candidateOrder ? formatFen(candidateOrder.totalFen) : "--"}</b>
              {candidate.reason ? <em>{candidate.reason}</em> : null}
            </label>
          );
        })}
      </div>

      <div className="combined-checkout-summary">
        <span>已选 {selectedFacts.length} 张台单</span>
        <strong>合计 {formatFen(totalFen)}</strong>
      </div>
      {selectionMessage ? <div className="disabled-reason">{selectionMessage}</div> : null}

      <fieldset
        className="combined-checkout-methods"
        disabled={!canCollect || recoveryLocked || submitting}
      >
        <legend>选择收款方式</legend>
        {(Object.keys(methodLabels) as CombinedCheckoutManualPaymentMethod[]).map((candidate) => (
          <button
            key={candidate}
            type="button"
            className={method === candidate ? "is-active" : ""}
            aria-pressed={method === candidate}
            onClick={() => {
              setMethod(candidate);
              setConfirmed(false);
              setFeedback(null);
            }}
          >
            {candidate === "CASH" ? <Banknote size={17} /> : null}
            {methodLabels[candidate]}
          </button>
        ))}
      </fieldset>
      <p className="combined-checkout-consequence">{methodConsequence(method)}</p>

      {canCollect ? (
        <>
          <label className="combined-checkout-confirmation">
            <input
              type="checkbox"
              checked={confirmed}
              disabled={operationDisabled}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            <span>
              我已逐张核对桌号、订单号和金额，并确认顾客使用{methodLabels[method]}支付合计
              {formatFen(totalFen)}。
            </span>
          </label>
          <button
            className="button primary"
            type="button"
            disabled={operationDisabled || !confirmed}
            onClick={submit}
          >
            <Check size={17} />
            {submitting ? "正在确认整批结果..." : "确认合并收款"}
          </button>
        </>
      ) : null}

      {recoveryView.status !== "NONE" ? (
        <section className="combined-checkout-recovery" aria-label="合并收款恢复">
          <div>
            <LockKeyhole size={18} />
            <strong>原合并收款尚未安全收口</strong>
          </div>
          {recoveryView.status === "OWNED" ? (
            <p>
              原请求包含 {recoveryView.recovery.command.request.orders.length} 张台单，合计
              {formatFen(recoveryView.recovery.command.request.expectedTotalFen)}，方式为
              {methodLabels[recoveryView.recovery.command.request.method]}。
            </p>
          ) : null}
          <div className="combined-checkout-recovery-actions">
            {canCollect && recoveryView.status === "OWNED" && recoveryView.canRetry ? (
              <button className="button primary" type="button" onClick={retryOriginal}>
                使用原请求号重试
              </button>
            ) : null}
            {managedRecoveryView === undefined &&
            recoveryView.status === "OWNED" &&
            recoveryView.recovery.state === "OUTCOME_UNKNOWN" ? (
              <button
                className="button secondary"
                type="button"
                onClick={() => void readAuthority()}
              >
                重新读取门店权威结果
              </button>
            ) : null}
          </div>
        </section>
      ) : null}

      {feedback ? (
        <output className={`checkout-split-feedback is-${feedback.kind.toLowerCase()}`}>
          {feedback.message}
        </output>
      ) : null}
      {!canCollect ? <div className="disabled-reason">当前岗位不能办理跨桌合并收款。</div> : null}
      {disabled ? (
        <div className="disabled-reason">
          当前门店或台单写入状态不安全，只能查看，不能合并收款。
        </div>
      ) : null}
    </section>
  );
}
